///
/// EarthBrowser
///
/// Created by Matt Giger on 1/20/2024
/// Copyright (c) 2024 Geodata Labs
///

/**
 * Represents a rectangular area in 2D space.
 */
export class rect {
	/**
	 * Creates a new rect instance.
	 * @param left - The x-coordinate of the left edge (default: 0)
	 * @param top - The y-coordinate of the top edge (default: 0)
	 * @param width - The width of the rectangle (default: 0)
	 * @param height - The height of the rectangle (default: 0)
	 */
	constructor(public left: number = 0, public top: number = 0, public width: number = 0, public height: number = 0) {}

	/**
	 * Checks if the rectangle is empty (has no area).
	 * @returns True if the rectangle is empty, false otherwise
	 */
	public get empty(): boolean {
		return this.width <= 0 || this.height <= 0
	}

	/**
	 * Checks if the rectangle is not empty (has area).
	 * @returns True if the rectangle is not empty, false otherwise
	 */
	public get notEmpty(): boolean {
		return this.width > 0 && this.height > 0
	}

	/**
	 * Gets the x-coordinate of the right edge of the rectangle.
	 * @returns The x-coordinate of the right edge
	 */
	public get right(): number {
		return this.left + this.width
	}

	/**
	 * Gets the y-coordinate of the bottom edge of the rectangle.
	 * @returns The y-coordinate of the bottom edge
	 */
	public get bottom(): number {
		return this.top + this.height
	}

	/**
	 * Checks if this rect is equal to another rect.
	 * @param other - The rect to compare with
	 * @returns True if the rects are equal, false otherwise
	 */
	public equals(other: rect): boolean {
		return this.left === other.left && this.top === other.top && this.width === other.width && this.height === other.height
	}

	/**
	 * Calculates the intersection of this rect with another rect.
	 * @param other - The rect to intersect with
	 * @returns A new rect representing the intersection
	 */
	public intersection(other: rect): rect {
		return new rect(
			Math.max(this.left, other.left),
			Math.max(this.top, other.top),
			Math.min(this.width, other.width),
			Math.min(this.height, other.height)
		)
	}

	/**
	 * Checks if this rect intersects with another rect.
	 * @param other - The rect to check intersection with
	 * @returns True if the rects intersect, false otherwise
	 */
	public intersects(other: rect): boolean {
		return !(
			other.left > this.right ||
			other.right < this.left ||
			other.top > this.bottom ||
			other.bottom < this.top
		)
	}

	/**
	 * Checks if a point is contained within the rect.
	 * @param x - The x-coordinate of the point
	 * @param y - The y-coordinate of the point
	 * @returns True if the point is contained, false otherwise
	 */
	public contains(x: number, y: number): boolean {
		return x >= this.left && x <= this.left + this.width && y >= this.top && y <= this.top + this.height
	}

	/**
	 * Checks if a point is contained within the rect, treating the rect as an offset from (0,0).
	 * @param x - The x-coordinate of the point
	 * @param y - The y-coordinate of the point
	 * @returns True if the point is contained, false otherwise
	 */
	public offsetContains(x: number, y: number): boolean {
		return x >= 0 && x < this.width && y >= 0 && y < this.height
	}

	/**
	 * Returns a string representation of the rect.
	 * @returns A string in the format "(left, top, (width, height))"
	 */
	public toString(): string {
		return `(${this.left}, ${this.top}, (${this.width}, ${this.height}))`
	}

	/**
	 * Creates a new rect that is inset from this rect by the specified amounts.
	 * @param dx - The amount to inset horizontally
	 * @param dy - The amount to inset vertically
	 * @returns A new rect inset from this rect
	 */
	public inset(dx: number, dy: number): rect {
		return new rect(this.left + dx, this.top + dy, this.width - 2 * dx, this.height - 2 * dy)
	}
}